/**
 * The Database screen when there is nothing to browse.
 *
 * Laid out after the Claude Design "Database" artboard's `none` and `blocked`
 * states: one hero that answers with what to do instead of with an error.
 * Neither case is a failure — most apps never bought the extra, and a blocked
 * database is a cancellation the customer asked for. Both end on the same
 * button, which opens the plan screen with the order already filled in.
 *
 * Every fact on it is read: the price and the pack size come from the extras
 * catalogue, the date the data goes comes from the purge the cancellation
 * scheduled. Where the artboard promised daily backups it is not followed —
 * we only back a database up on the events that destroy it
 * (`docs/containers-database.md` §7) — so the card says what actually happens.
 */
import { ContainerFeature, Plan } from '@back4app2/sdk';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import back4app2 from '../../back4app2';
import { DsButton, Eyebrow, StatusPill, SurfaceCard } from '../../components/ds';
import { ChevronRightIcon, DatabaseIcon } from '../../components/ds/icons';
import { formatBytes } from './cells';

const TITLE = 'm-0 font-sora text-ds-20 sm:text-ds-36 font-semibold tracking-[-0.02em] leading-[1.15] text-white';
/**
 * Its own space above, rather than the column's even gap: a 36px headline and
 * the sentence explaining it were sitting as close together as two list items,
 * which read as one block of text instead of a title and its answer.
 */
const LEAD = 'mx-0 mb-0 mt-2 max-w-[64ch] text-ds-15 leading-[1.5] text-navy-200';
const FOOT_NOTE = 'font-courrier text-ds-115 text-navy-300';
/** The hero shell both unavailable states share. */
const HERO = 'flex w-full max-w-[880px] py-2 sm:py-6';
const HERO_COPY = 'flex flex-col gap-2.5 min-w-0 max-w-[58ch]';
const FACTS = 'grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3.5 sm:gap-5 w-full mt-1.5 pt-4 border-t border-navy-700';
const LIST = 'flex flex-col w-full mt-0.5 pt-1 border-t border-navy-700';
const ACTIONS = 'flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 w-full mt-1.5 pt-4 border-t border-navy-700';

/**
 * The database extra as it is sold today — the price that goes on the button,
 * and how much storage one pack is.
 *
 * Null while it loads AND on failure: the catalogue is a nicety here, so the
 * card renders the same offer without a price rather than an error. The two
 * states this file draws are the only ones that ask for it.
 */
const useDatabaseFeature = (): { feature: ContainerFeature | null, entryPlan?: Plan } => {
  const [feature, setFeature] = useState<ContainerFeature | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([back4app2.containerFeatures(), back4app2.getPlans()])
      .then(([features, rows]) => {
        if (cancelled) {
          return;
        }
        setFeature(features.find(candidate => candidate.kind === 'database') || null);
        setPlans(rows);
      })
      .catch(() => { /* the offer stands without a price or a plan name */ });

    return () => { cancelled = true; };
  }, []);

  // The cheapest plan this extra is actually sold on — read, not written down:
  // which plans carry it is a catalogue decision that changes without us.
  const entryPlan = plans
    .filter(candidate => candidate.price > 0 && (feature?.planIds || []).includes(candidate.id))
    .sort((a, b) => a.price - b.price)[0];

  return { feature, entryPlan };
};

/**
 * The one button both states carry: the screen that sells the extra — with
 * the order already filled in.
 *
 * `?add=database` is the whole request. This button names a plan and a price,
 * and landing on a table where nothing is chosen makes the reader redo the
 * decision they just made — but what they decided was "I want a database",
 * not a pair of UUIDs, so that is what the link says. Which plan sells it and
 * which row it is are looked up on the other side, where the catalogue
 * already is. The link seeds the form; it does not submit it.
 */
const PlanLink = ({ appId, children }: { appId?: string, children: React.ReactNode }) => {
  if (!appId) {
    return null;
  }

  return (
    <Link to={`/apps/${appId}/settings?add=database#plan`} className="ml-auto flex-none">
      <DsButton variant="primary" size="lg">{children}</DsButton>
    </Link>
  );
};

/** `1.0 GB` → `1 GB`. A pack that is a round number should read as one. */
const packSize = (bytes: number): string => formatBytes(bytes).replace('.0 ', ' ');

/**
 * What the extra actually is, once. Three claims, each one true of what we
 * run: the design's fourth — "daily backups, kept 7 days" — is not, so it is
 * not here. A database is backed up on the events that DESTROY it and nowhere
 * else (`docs/containers-database.md` §7), and a promise of nightly backups on
 * the screen that sells it is the worst place to be wrong.
 */
const CLAIMS = [
  {
    title: 'Managed, not self-hosted',
    note: 'We run it, patch it, and keep it reachable from your container and nothing else.',
  },
  {
    title: 'Connected without config',
    note: 'DATABASE_URL is injected at build and at run time — you never paste a password.',
  },
  {
    title: 'Browsable from this screen',
    note: 'Tables, rows, columns, indexes and triggers, right here.',
  },
];

/**
 * The offer, as a hero rather than a card: on a screen with nothing to browse
 * the whole region is free, and the thing being sold deserves it.
 *
 * Two audiences share it. An app on Free cannot hold a database at all, so it
 * is told which plan starts one; an app already paying only needs the extra.
 * Both land on the same screen.
 */
const NoDatabaseCard = ({ appId, plan }: { appId?: string, plan?: Plan }) => {
  const { feature, entryPlan } = useDatabaseFeature();
  const isOnFreePlan = !plan || plan.price === 0;

  const facts = [
    feature?.maxStorageBytes ? {
      label: 'storage',
      value: packSize(feature.maxStorageBytes).split(' ')[0],
      unit: `${packSize(feature.maxStorageBytes).split(' ')[1]} to start`,
    } : undefined,
    // No engine row. Which server runs it is ours to choose and ours to
    // upgrade, and naming it here turned an implementation detail into part of
    // what is being bought.
    { label: 'run for you', value: 'Managed', unit: 'backed up on teardown' },
    feature ? { label: 'added to bill', value: `$${feature.price}`, unit: '/mo' } : undefined,
  ].filter((fact): fact is NonNullable<typeof fact> => !!fact);

  return (
    <div className="flex w-full max-w-[880px] py-2 sm:py-6">
      {/* No illustration: an 88px outline of a database floating beside the
          copy carried no information the headline did not, and on this screen
          it read as a stray glyph rather than as art. */}
      <div className="flex flex-col gap-2.5 min-w-0 max-w-[58ch]">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill tone="info">
            {isOnFreePlan ? 'Not available on Free plan' : 'Not added yet'}
          </StatusPill>
        </div>

        <h2 className={TITLE}>
          {isOnFreePlan
            ? `A database starts on the ${entryPlan?.name || 'paid'} plan`
            : 'This app has no database yet'}
        </h2>
        <p className={LEAD}>
          {isOnFreePlan
            ? 'The Free plan runs your container but gives it nowhere to store data. '
            : ''}
          Add the database extra and we create a managed database next to this app and
          inject <code className="font-courrier text-navy-100">DATABASE_URL</code> at build
          and run time.
        </p>

        {/* Three, not the design's four: how it connects was the fourth, and
            the list right below already says it in a full sentence. The track
            is auto-fit so a value never has to share a column narrower than
            it is — that is what had "DATABASE_URL" sitting on top of "$5". */}
        <div className={FACTS}>
          {facts.map(fact => (
            <div key={fact.label} className="flex flex-col gap-[5px] min-w-0">
              <Eyebrow>{fact.label}</Eyebrow>
              <div className="flex items-baseline gap-1.5 min-w-0">
                <span className="flex-none font-sora text-ds-20 font-semibold tracking-[-0.02em] text-white">
                  {fact.value}
                </span>
                <span className={`min-w-0 truncate ${FOOT_NOTE}`}>{fact.unit}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col w-full mt-0.5 pt-1 border-t border-navy-700">
          {CLAIMS.map((claim, index) => (
            <div
              key={claim.title}
              className={`flex items-start gap-2.5 py-3 ${index ? 'border-t border-navy-700' : ''}`}
            >
              <span className="flex-none flex items-center justify-center w-4 h-5 text-navy-300">
                <ChevronRightIcon size={14} />
              </span>
              <span className="flex flex-col gap-[3px] min-w-0">
                <span className="text-ds-135 font-medium text-white">{claim.title}</span>
                <span className="text-ds-125 text-navy-200">{claim.note}</span>
              </span>
            </div>
          ))}
        </div>

        {/* One button. The design pairs it with "Compare plans", but here that
            is the same screen — the plan table IS the comparison. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 w-full mt-1.5 pt-4 border-t border-navy-700">
          <PlanLink appId={appId}>
            {isOnFreePlan
              ? `Upgrade to ${entryPlan?.name || 'a paid plan'}`
              : feature ? `Add a database — $${feature.price}/mo` : 'Add a database'}
          </PlanLink>
        </div>
      </div>
    </div>
  );
};

/** What being blocked actually means, in the order it matters. */
const BLOCKED_CLAIMS = [
  {
    title: 'Your data is still here',
    note: 'Tables, rows and indexes are untouched until the keep date.',
  },
  {
    title: 'The app cannot connect',
    note: 'Every query from your container fails while the database is blocked.',
  },
  {
    title: 'Reactivating restores it in place',
    note: 'The same database comes back on your next deploy — nothing to import.',
  },
];

/**
 * Canceled, not gone. The app's role is NOLOGIN and this screen cannot read
 * the database — but the data is there, and what the reader needs is the date
 * it stops being there and what happens on either side of it.
 *
 * The same hero as the offer above: they are the two halves of one decision,
 * and a customer who reads both should not have to learn two layouts.
 */
const BlockedCard = ({ appId, purgeScheduledAt }: {
  appId?: string,
  purgeScheduledAt?: Date | null,
}) => {
  const goesAt = purgeScheduledAt ? new Date(purgeScheduledAt) : undefined;

  const facts = [
    goesAt ? {
      label: 'data kept until',
      value: goesAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      unit: String(goesAt.getFullYear()),
      className: 'text-state-warn',
    } : undefined,
    {
      // Not the design's "last backup — taken daily": a database here is
      // backed up on the way out and at no other time, and the date of a
      // nightly backup we do not take is not ours to print.
      label: goesAt ? 'after that date' : 'when that runs out',
      value: 'Backed up',
      unit: 'then dropped',
      className: 'text-white',
    },
  ].filter((fact): fact is NonNullable<typeof fact> => !!fact);

  return (
    <div className={HERO}>
      <div className={HERO_COPY}>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <StatusPill tone="warn">Blocked</StatusPill>
        </div>

        <h2 className={TITLE}>This database is blocked</h2>
        <p className={LEAD}>
          The plan that paid for it was canceled, so your app cannot connect to it and
          this screen cannot read it. Nothing has been deleted.
        </p>

        <div className={FACTS}>
          {facts.map(fact => (
            <div key={fact.label} className="flex flex-col gap-[5px] min-w-0">
              <Eyebrow>{fact.label}</Eyebrow>
              <div className="flex items-baseline gap-1.5 min-w-0">
                <span className={`flex-none font-sora text-ds-20 font-semibold tracking-[-0.02em] ${fact.className}`}>
                  {fact.value}
                </span>
                <span className={`min-w-0 truncate ${FOOT_NOTE}`}>{fact.unit}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={LIST}>
          {BLOCKED_CLAIMS.map((claim, index) => (
            <div
              key={claim.title}
              className={`flex items-start gap-2.5 py-3 ${index ? 'border-t border-navy-700' : ''}`}
            >
              <span className="flex-none flex items-center justify-center w-4 h-5 text-navy-300">
                <ChevronRightIcon size={14} />
              </span>
              <span className="flex flex-col gap-[3px] min-w-0">
                <span className="text-ds-135 font-medium text-white">{claim.title}</span>
                <span className="text-ds-125 text-navy-200">{claim.note}</span>
              </span>
            </div>
          ))}
        </div>

        <div className={ACTIONS}>
          <PlanLink appId={appId}>Reactivate plan</PlanLink>
        </div>
      </div>
    </div>
  );
};

/** The two momentary states, which are told rather than sold. */
const PlainCard = ({ title, blurb }: { title: string, blurb: string }) => (
  <SurfaceCard tone="surface" className="p-6">
    <div className="flex items-start gap-3">
      <DatabaseIcon size={20} className="text-navy-300 mt-0.5" />
      <div>
        <p className="text-ds-135 text-navy-100">{title}</p>
        <p className="mt-1 max-w-xl text-ds-125 text-navy-300">{blurb}</p>
      </div>
    </div>
  </SurfaceCard>
);

const UnavailableCard = ({ appId, plan, reason, purgeScheduledAt }: {
  appId?: string,
  /** What the app runs on today — a database is not sold on Free. */
  plan?: Plan,
  reason?: string | null,
  purgeScheduledAt?: Date | null,
}) => {
  if (reason === 'CANCELED') {
    return <BlockedCard appId={appId} purgeScheduledAt={purgeScheduledAt} />;
  }

  if (reason === 'APP_NOT_RUNNING') {
    return <PlainCard
      title="This app is being removed."
      blurb="Its database is not available."
    />;
  }

  if (reason === 'UNREACHABLE') {
    return <PlainCard
      title="The database is there, but we couldn't reach it."
      blurb="This is usually momentary. Reload in a minute — your app's own connection is unaffected by this screen."
    />;
  }

  return <NoDatabaseCard appId={appId} plan={plan} />;
};

export default UnavailableCard;
